import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { db, auth } from "../../firebase";
import { useAuthState } from "react-firebase-hooks/auth";
import { serverTimestamp } from "firebase/firestore";
import Window from "../../components/Window";
import Icon from "../../components/Icon";
import { Avatar } from "../../components/ProfileBits";
import { useBlocks } from "../../blocks";
import { PenPal, getMyPenPals, getUsername, pacedAdd } from "../../penpal";
import PromptIdea from "../../components/PromptIdea";
import { LETTER_PROMPTS } from "../../prompts";
import { isSlowDown } from "../../pace";

// Letters can only go to pen pals (people who accepted a request, or whose
// request you accepted). Pick one from your address book, or arrive here
// from a "Write" button with ?to=<their id>.
const MessageForm = () => {
  const [user] = useAuthState(auth);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { ids: blocked } = useBlocks();

  const [pals, setPals] = useState<PenPal[] | null>(null);
  const [to, setTo] = useState(searchParams.get("to") || "");
  const [subject, setSubject] = useState(searchParams.get("subject") || "");
  const [body, setBody] = useState("");
  const [cw, setCw] = useState("");
  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!user) return;
    getMyPenPals(user.uid)
      .then(setPals)
      .catch((err) => {
        console.error(err);
        setPals([]);
      });
  }, [user]);

  const available = (pals || []).filter((p) => !blocked.has(p.id));
  const recipient = available.find((p) => p.id === to);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !recipient) return;
    if (!subject.trim() || !body.trim()) {
      setError("Please fill out all fields.");
      return;
    }

    setSending(true);
    setError("");
    try {
      await pacedAdd(user.uid, "letter", "messages", {
        senderId: user.uid,
        senderUsername: await getUsername(user.uid),
        receiverId: recipient.id,
        receiverUsername: recipient.username,
        subject: subject.trim().slice(0, 200),
        body: body.trim(),
        ...(cw.trim() ? { cw: cw.trim().slice(0, 100) } : {}),
        timestamp: serverTimestamp(),
      });
      setSuccess(true);
      setTimeout(() => navigate("/inbox"), 1500);
    } catch (err) {
      if (isSlowDown(err)) return; // the wait note is showing
      console.error(err);
      setError(
        user.emailVerified
          ? "Sorry, your letter couldn't be sent. Please try again."
          : "Please verify your email first. Use the reminder at the top of the page to resend the link."
      );
    } finally {
      setSending(false);
    }
  };

  if (pals === null) {
    return <p className="empty">Finding your address book...</p>;
  }

  // Asked to write to someone who isn't (or is no longer) a pen pal.
  if (to && !recipient) {
    return (
      <div className="page-narrow">
        <Window title="compose_letter.exe" tone="tan" minimizable={false}>
          <h1 className="window-heading">Not pen pals yet</h1>
          <p className="window-sub">
            Letters are just between pen pals. Send them a pen pal request
            first. Once they accept, you can write to each other.
          </p>
          <div className="btn-row">
            <Link to={`/profile/${to}`} className="btn">
              <Icon name="request" size={16} /> Go to their profile
            </Link>
            <Link to="/address-book" className="btn btn-plain">
              <Icon name="book" size={16} /> My address book
            </Link>
          </div>
        </Window>
      </div>
    );
  }

  return (
    <div className="page-narrow">
      <Window title="compose_letter.exe" tone="tan" minimizable={false}>
        <h1 className="window-heading flex items-center gap-2">
          <Icon name="edit" size={26} tone="pink" pop /> Write a letter
        </h1>

        {available.length === 0 ? (
          <div className="flex flex-col items-start gap-3">
            <p className="window-sub !mb-0">
              Your address book is empty for now. Letters are just between pen
              pals, so find someone on the board and send them a pen pal
              request.
            </p>
            <Link to="/board" className="btn">
              <Icon name="pin" size={16} /> Go to the PenPal Board
            </Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <label className="field">
              <span className="field-label">To</span>
              <div className="flex items-center gap-3">
                {recipient && (
                  <Avatar
                    config={recipient.avatarConfig}
                    icon={recipient.avatar}
                    tone={recipient.avatarTone}
                    size={44}
                  />
                )}
                <select
                  className="input"
                  value={to}
                  onChange={(e) => setTo(e.target.value)}
                  required
                >
                  <option value="">Choose a pen pal...</option>
                  {available.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.displayName ? `${p.displayName} (@${p.username})` : `@${p.username}`}
                    </option>
                  ))}
                </select>
              </div>
            </label>
            {searchParams.get("reveal") === "1" && (
              <p className="notice flex items-start gap-2 text-sm">
                <Icon name="eye" size={16} className="mt-0.5" />
                <span>
                  They found you through your anonymous ad. Writing back will
                  show them your username.
                </span>
              </p>
            )}
            <label className="field">
              <span className="field-label">Subject</span>
              <input
                type="text"
                className="input"
                value={subject}
                maxLength={200}
                onChange={(e) => setSubject(e.target.value)}
                required
              />
            </label>
            <label className="field">
              <span className="field-label">Content warning (optional)</span>
              <input
                type="text"
                className="input"
                value={cw}
                maxLength={100}
                onChange={(e) => setCw(e.target.value)}
                placeholder="e.g. grief, medical stuff"
              />
              <span className="text-xs text-ink-soft">
                Your pen pal will see this first and choose when to read the letter.
              </span>
            </label>
            <label className="field">
              <span className="field-label">Your letter</span>
              <textarea
                className="input lined-paper !min-h-[14rem] font-hand !text-lg"
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder="Dear friend,"
                required
              />
            </label>
            <PromptIdea prompts={LETTER_PROMPTS} />

            {error && <p className="error-text">{error}</p>}
            {success && (
              <p className="success-text">Letter sent! Taking you to your inbox...</p>
            )}

            <div className="btn-row">
              <button type="submit" className="btn" disabled={success || sending || !recipient}>
                <Icon name="send" size={16} /> Send letter
              </button>
              <button type="button" onClick={() => navigate(-1)} className="btn btn-plain">
                Cancel
              </button>
            </div>
          </form>
        )}
      </Window>
    </div>
  );
};

export default MessageForm;
